/**
 * iSale i18n runtime — minimal, dependency-free.
 *
 * Why not `i18next`? It's a great library but adds ~30 KB to the mobile
 * bundle. We need just three things for MVP:
 *   1. Key lookup with dotted paths (e.g. `t("home.greetingMorning")`)
 *   2. Variable interpolation (`t("facts.cta", { n: 2 })`)
 *   3. A subscribable locale state so React components re-render
 *
 * All three are ~80 LoC. Swap-in for i18next when scale demands ICU
 * plurals + gender + interpolation tree-shake.
 *
 * Mobile + web each create a `t()` hook (see `apps/<surface>/src/shared/i18n`)
 * that wraps this runtime + persists to AsyncStorage / localStorage.
 */

import vi, { type TranslationBundle } from "./locales/vi";
import en from "./locales/en";
import zh from "./locales/zh";

export type Locale = "vi" | "en" | "zh";

/**
 * Looser bundle type used at the lookup layer — we keep the strict
 * `TranslationBundle` literal type on vi (the source of truth) but
 * accept any object with the same shape for en/zh so their distinct
 * string values type-check against the runtime resolver.
 */
type LooseBundle = {
  readonly [k: string]: string | LooseBundle;
};

const bundles: Record<Locale, LooseBundle> = {
  vi: vi as unknown as LooseBundle,
  en: en as unknown as LooseBundle,
  zh: zh as unknown as LooseBundle,
};

export const SUPPORTED_LOCALES: Locale[] = ["vi", "en", "zh"];

export const LOCALE_LABELS: Record<Locale, string> = {
  vi: "Tiếng Việt",
  en: "English",
  zh: "中文 (简体)",
};

/** ISO locale tags for use with Intl APIs. */
export const INTL_LOCALES: Record<Locale, string> = {
  vi: "vi-VN",
  en: "en-US",
  zh: "zh-CN",
};

/**
 * Locale-aware currency formatting. VN uses VND with thousands
 * separators ("199.000₫"); USD + CNY follow standard Intl conventions.
 */
export function formatCurrency(value: number, locale: Locale): string {
  const currency = locale === "vi" ? "VND" : locale === "en" ? "USD" : "CNY";
  try {
    return new Intl.NumberFormat(INTL_LOCALES[locale], {
      style: "currency",
      currency,
      maximumFractionDigits: currency === "VND" ? 0 : 2,
    }).format(value);
  } catch {
    return `${value}`;
  }
}

/** Locale-aware date formatting. */
export function formatDate(
  date: Date | string,
  locale: Locale,
  opts?: Intl.DateTimeFormatOptions,
): string {
  const d = typeof date === "string" ? new Date(date) : date;
  try {
    return new Intl.DateTimeFormat(INTL_LOCALES[locale], opts).format(d);
  } catch {
    return d.toISOString().slice(0, 10);
  }
}

/** Dotted-path key lookup. Returns the key itself when unresolved. */
function resolveKey(bundle: unknown, path: string[]): string | null {
  let cur: unknown = bundle;
  for (const seg of path) {
    if (cur && typeof cur === "object" && seg in (cur as Record<string, unknown>)) {
      cur = (cur as Record<string, unknown>)[seg];
    } else {
      return null;
    }
  }
  return typeof cur === "string" ? cur : null;
}

/** {{var}} interpolation with safe HTML-escape skip. */
function interpolate(s: string, vars: Record<string, string | number>): string {
  return s.replace(/\{\{(\w+)\}\}/g, (_, name) =>
    name in vars ? String(vars[name]) : `{{${name}}}`,
  );
}

/**
 * Core translator. Looks up `key` in the given bundle; falls back to
 * Vietnamese on miss; falls back to the key itself if VI also misses
 * (only happens if the key was mistyped — i18n parity check catches
 * this in tests).
 */
export function translate(
  locale: Locale,
  key: string,
  vars?: Record<string, string | number>,
): string {
  const path = key.split(".");
  const fromLocale = resolveKey(bundles[locale], path);
  const value = fromLocale ?? resolveKey(bundles.vi, path) ?? key;
  if (!vars) return value;
  return interpolate(value, vars);
}

/** Sync-safe key validator — used by tests to assert bundle parity. */
export function listKeys(locale: Locale): string[] {
  const out: string[] = [];
  function walk(obj: unknown, prefix: string[]): void {
    if (!obj || typeof obj !== "object") return;
    for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
      const nextPath = [...prefix, k];
      if (typeof v === "string") out.push(nextPath.join("."));
      else walk(v, nextPath);
    }
  }
  walk(bundles[locale], []);
  return out;
}

export { vi, en, zh };
export type { TranslationBundle };
